import { useMemo } from "react";
import { getUserCurrencySymbol } from "utils/AuthUtil";
import { is_empty } from "utils/validations";
import { roundOff } from "utils/Utils";
import { SALES_AND_COMMISSION_CONSTANTS } from "./SalesAndCommission.constants";

export const useSalesAndCommission = (referralOverview) => {
  const {
    DEFAULT_COMMISSION,
    DEFAULT_SALES_COUNT,
    DEFAULT_SALES_AMOUNT,
    DEFAULT_CURRENCY_SYMBOL,
    DECIMAL_PRECISION,
    PERCENTAGE_MULTIPLIER,
    SINGLE_COUNT_THRESHOLD,
    SALES_TEXT_SINGULAR,
    SALES_TEXT_PLURAL,
  } = SALES_AND_COMMISSION_CONSTANTS;

  return useMemo(() => {
    const currencySymbol = getUserCurrencySymbol() || DEFAULT_CURRENCY_SYMBOL;
    const isLoading = !referralOverview;

    // Commission earned: extracted from overview total_amount_earned
    const commissionValue = !is_empty(referralOverview?.total_amount_earned)
      ? roundOff(referralOverview?.total_amount_earned, DECIMAL_PRECISION)
      : DEFAULT_COMMISSION;

    // Sales count: sales_count, converted_referrals, or total_referrals
    const salesCount = Number(
      referralOverview?.sales_count ??
        referralOverview?.converted_referrals ??
        referralOverview?.total_sales ??
        DEFAULT_SALES_COUNT
    );

    // Sales amount: extracted or computed from total_amount_earned and commission_rate
    const calculateSalesAmount = () => {
      const explicitAmount =
        referralOverview?.total_sales_amount ?? referralOverview?.sales_amount;
      if (!is_empty(explicitAmount)) {
        return roundOff(explicitAmount, DECIMAL_PRECISION);
      }

      const { total_amount_earned, commission_rate } = referralOverview || {};
      const rateNum = Number(commission_rate);
      if (!is_empty(total_amount_earned) && rateNum > 0) {
        const derivedAmount =
          (Number(total_amount_earned) * PERCENTAGE_MULTIPLIER) / rateNum;
        return roundOff(derivedAmount, DECIMAL_PRECISION);
      }

      return DEFAULT_SALES_AMOUNT;
    };

    const totalSalesAmount = calculateSalesAmount();

    const salesText =
      salesCount === SINGLE_COUNT_THRESHOLD
        ? SALES_TEXT_SINGULAR
        : SALES_TEXT_PLURAL;

    return {
      currencySymbol,
      isLoading,
      commissionValue,
      salesCount,
      totalSalesAmount,
      salesText,
    };
  }, [
    referralOverview,
    DEFAULT_COMMISSION,
    DEFAULT_SALES_COUNT,
    DEFAULT_SALES_AMOUNT,
    DEFAULT_CURRENCY_SYMBOL,
    DECIMAL_PRECISION,
    PERCENTAGE_MULTIPLIER,
    SINGLE_COUNT_THRESHOLD,
    SALES_TEXT_SINGULAR,
    SALES_TEXT_PLURAL,
  ]);
};
